import type { ColumnDef, Row } from "@tanstack/react-table";

import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, FormInput, IconBin, IconPlus, Table } from "@sentricmusic/ui";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns/esm";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Suggestion } from "@/api/queries";

import { useUpdateSuggestion } from "@/api/mutations";
import { useUIStateStore } from "@/store/uiStateStore";

import SongSuggestionActionModal from "./SongSuggestionActionModal";

type SuggestionsTableProps = {
  data: Suggestion[];
  highlightColour?: string;
  isLoading?: boolean;
};

type CellProps = {
  row: Row<Suggestion>;
};

function suggestionImageUrl(suggestion: Suggestion): string | null {
  const { images, source } = suggestion;
  if (images == null)
    return null;

  if (source === "Spotify" && typeof images === "string") {
    try {
      const parsed = JSON.parse(images) as { url?: string }[];
      return parsed[2]?.url ?? null;
    }
    catch {
      return null;
    }
  }

  if ((source === "TuneCore" || source === "TuneCore & Spotify") && typeof images === "object") {
    return images.small_url ?? null;
  }

  return null;
}

function SongTitleCell({ row }: CellProps) {
  const image = suggestionImageUrl(row.original);
  const title = row.original.title;

  return (
    <div className="flex items-center gap-3">
      {image != null && (
        <div className="w-10 h-10 shrink-0">
          <img
            alt="thumb"
            title="thumb"
            src={image}
            className="object-cover w-10 h-10 rounded"
          />
        </div>
      )}
      <span className="flex-1">{title}</span>
    </div>
  );
}

function ReleaseDateCell({ row }: CellProps) {
  const releaseDate = row.original.release_date;
  return releaseDate != null && releaseDate !== "0000-00-00"
    ? format(Date.parse(releaseDate), "yyyy-MM-dd")
    : null;
}

function ArtistsCell({ row }: CellProps) {
  return row.original.artists.replace(/,/g, ", ").replace(/&#44;/g, ",");
}

type SuggestionActionsCellProps = CellProps & {
  onAddSong: (suggestion: Suggestion) => void;
  onDeleteSuggestion: (suggestion: Suggestion) => void;
};

function SuggestionActionsCell({ row, onAddSong, onDeleteSuggestion }: SuggestionActionsCellProps) {
  const { t } = useTranslation(["songs"]);

  return (
    <div className="flex justify-end gap-2 p-2">
      <Button
        variant="iconAction"
        label={<IconPlus size="md" className="stroke-ink-primary" />}
        aria-label={String(t("songs:add-song-suggestion-action", "Add Song"))}
        tooltip
        tooltipText={String(t("songs:add-song-suggestion-action", "Add Song"))}
        onClick={() => onAddSong(row.original)}
      />
      <Button
        variant="iconAction"
        label={<IconBin size="md" className="stroke-ink-primary" />}
        aria-label={String(t("songs:remove-suggestion"))}
        tooltip
        tooltipText={String(t("songs:remove-suggestion"))}
        onClick={() => onDeleteSuggestion(row.original)}
      />
    </div>
  );
}

function SuggestionsTable({ data, highlightColour, isLoading }: SuggestionsTableProps) {
  const { t } = useTranslation(["songs", "common"]);
  const flags = useFlags();
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionSuggestion, setActionSuggestion] = useState<Suggestion | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Suggestion | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const queryClient = useQueryClient();
  const updateSuggestionMutation = useUpdateSuggestion();

  const invalidateSuggestions = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["getSuggestions"], exact: false });
  }, [queryClient]);

  const handleAddSong = useCallback((suggestion: Suggestion) => {
    setActionSuggestion(suggestion);
  }, []);

  const handleDeleteSuggestion = useCallback((suggestion: Suggestion) => {
    setDeleteTarget(suggestion);
  }, []);

  const confirmDeleteSuggestion = useCallback(() => {
    if (!deleteTarget)
      return;

    setIsDeleting(true);
    updateSuggestionMutation.mutate({ status: 3, id: deleteTarget.id }, {
      onSuccess: () => {
        invalidateSuggestions();
        setIsDeleting(false);
        setDeleteTarget(null);
      },
      onError: (error: Error) => {
        console.error(`update suggestion ${error.message}`);
        setIsDeleting(false);
      },
    });
  }, [deleteTarget, updateSuggestionMutation, invalidateSuggestions]);

  const renderActionsCell = useCallback(
    ({ row }: CellProps) => (
      <SuggestionActionsCell row={row} onAddSong={handleAddSong} onDeleteSuggestion={handleDeleteSuggestion} />
    ),
    [handleAddSong, handleDeleteSuggestion],
  );

  const columns = useMemo((): ColumnDef<Suggestion>[] => [
    {
      id: "title",
      accessorKey: "title",
      header: String(t("songs:spotify-table-name")),
      size: 280,
      cell: ({ row }) => <SongTitleCell row={row} />,
    },
    {
      id: "release_date",
      accessorKey: "release_date",
      header: String(t("songs:release-date")),
      size: 160,
      cell: ({ row }) => <ReleaseDateCell row={row} />,
    },
    {
      id: "artists",
      accessorKey: "artists",
      header: String(t("songs:spotify-table-performers")),
      size: 220,
      cell: ({ row }) => <ArtistsCell row={row} />,
    },
    {
      id: "source",
      accessorKey: "source",
      header: String(t("songs:spotify-table-suggested-by")),
      size: 150,
    },
    {
      id: "actions",
      header: "",
      size: 100,
      minSize: 100,
      maxSize: 100,
      enableSorting: false,
      enablePinning: true,
      meta: { pinSide: "right" },
      cell: renderActionsCell,
    },
  ], [t, renderActionsCell]);

  const filteredData = useMemo(() => {
    const sorted = [...data].sort((a, b) => a.popularity - b.popularity);
    if (searchTerm === "")
      return sorted;
    const term = searchTerm.toLowerCase();
    return sorted.filter(
      s => s.title.toLowerCase().includes(term) || s.artists.toLowerCase().includes(term),
    );
  }, [data, searchTerm]);

  const filterContent = (
    <FormInput
      smallFont
      label={String(t("songs:spotify-table-name"))}
      placeholder={String(t("common:search", "Search"))}
      value={searchTerm}
      onChange={e => setSearchTerm(e.target.value)}
    />
  );

  if (flags["pd-spotify-suggestions-feature"] !== true) {
    return null;
  }

  return (
    <>
      <Table
        data={filteredData}
        defaultPageSize={10}
        columns={columns}
        highlightColour={highlightColour}
        filterContent={filterContent}
        onClear={() => setSearchTerm("")}
        getRowId={row => row.id}
        noDataMessage={String(t("songs:spotify-table-none-message"))}
        isLoading={isLoading}
      />

      {actionSuggestion && (
        <SongSuggestionActionModal
          suggestion={actionSuggestion}
          open={actionSuggestion != null}
          onOpenChange={(open) => {
            if (!open)
              setActionSuggestion(null);
          }}
          onSuggestionResolved={invalidateSuggestions}
        />
      )}

      <Dialog
        open={deleteTarget != null}
        onOpenChange={(open) => {
          if (!open && !isDeleting)
            setDeleteTarget(null);
        }}
      >
        <DialogContent className={isTunecore ? "bg-layer-inverse text-white border-canvas-inverse" : ""}>
          <DialogHeader>
            <DialogTitle>{String(t("songs:remove-suggestion"))}</DialogTitle>
            <DialogDescription>
              {t("songs:remove-suggestion-confirmation", {
                defaultValue: "Are you sure you want to remove \"{{title}}\" from your suggestions?",
                title: deleteTarget?.title ?? "this suggestion",
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant={isTunecore ? "primary" : "secondary"}
              label={String(t("common:cancel", "Cancel"))}
              onClick={() => setDeleteTarget(null)}
              disabled={isDeleting}
            />
            <Button
              variant={isTunecore ? "secondary" : "primary"}
              label={String(t("songs:remove-suggestion"))}
              onClick={() => confirmDeleteSuggestion()}
              disabled={isDeleting}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SuggestionsTable;
